import { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { Autocomplete } from "@react-google-maps/api";
import { db, storage } from "../../services/firebase";
import DriverMap from "../driver/DriverMap";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { inputClass, labelClass, fieldWrapClass } from "./mixedCompanyAssignJobs/styles";
import {
  createTempId,
  emptyDefaultStop,
  toAbbrevCode,
  getCompanyUiVariant,
  makeEmptyStopForCompany,
  getTruckDisplayLabel,
  getDriverDisplayLabel,
  getSiteContactName,
  getSiteContactPhone,
  getSiteContactEmail,
} from "./mixedCompanyAssignJobs/helpers";
import StatPill from "./mixedCompanyAssignJobs/StatPill";
import SectionCard from "./mixedCompanyAssignJobs/SectionCard";
import SearchableSelect from "./mixedCompanyAssignJobs/SearchableSelect";
import LiveSearchSelect from "./mixedCompanyAssignJobs/LiveSearchSelect";


export default function MixedCompanyAssignJobs({
  companies,
  drivers,
  scriptLoaded,
  onContextChange,
}) {
  const [driverUid, setDriverUid] = useState("");
  const [localDate, setLocalDate] = useState(() =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Melbourne" }),
  );
  const [runs, setRuns] = useState(() => [
    {
      id: "run-1",
      label: "Run #1",
      companyId: "",
      vehicleId: "",
      stops: [emptyDefaultStop()],
    },
  ]);
  const [docketFiles, setDocketFiles] = useState({});
  const [jobTypesByCompany, setJobTypesByCompany] = useState({});
  const [customersByCompany, setCustomersByCompany] = useState({});
  const [trucksByCompany, setTrucksByCompany] = useState({});
  const [openStopKeys, setOpenStopKeys] = useState(() => new Set());

  const toggleStopOpen = (runId, idx) => {
    const key = `${runId}__${idx}`;
    setOpenStopKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const acRefs = useRef({});

  const driver = useMemo(
    () => drivers.find((d) => d.uid === driverUid) || null,
    [drivers, driverUid],
  );

  const companyNameById = useMemo(() => {
    const map = {};
    (companies || []).forEach((company) => {
      map[company.id] = company.name || company.id;
    });
    return map;
  }, [companies]);

  const driverOptions = useMemo(
    () =>
      (drivers || []).map((d) => {
        const companyIds = [
          d.companyId,
          ...(Array.isArray(d.companyIds) ? d.companyIds : []),
        ].filter(Boolean);
        const companyNames = companyIds
          .map((companyId) => companyNameById[companyId] || companyId)
          .filter(Boolean);

        return {
          value: d.uid,
          label: getDriverDisplayLabel(d),
          description: companyNames.join(" | "),
          searchText: [
            getDriverDisplayLabel(d),
            d.uid,
            d.email,
            d.name,
            d.displayName,
            ...companyIds,
            ...companyNames,
          ]
            .filter(Boolean)
            .join(" "),
        };
      }),
    [companyNameById, drivers],
  );

  const draftKey = useMemo(() => {
    if (!driverUid || !localDate) return null;
    return `assignMixed_${driverUid}_${localDate}`;
  }, [driverUid, localDate]);

  useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setRuns(
          parsed.map((run, runIndex) => ({
            ...run,
            id: run.id || `run-${runIndex + 1}-${Date.now()}`,
            label: run.label || `Run #${runIndex + 1}`,
            companyId: run.companyId || "",
            vehicleId: run.vehicleId || "",
            stops: Array.isArray(run.stops)
              ? run.stops.map((stop) => ({
                  ...stop,
                  _tempId: stop?._tempId || createTempId(),
                }))
              : [emptyDefaultStop()],
          })),
        );
      }
    } catch {
      // ignore
    }
  }, [draftKey]);

  const saveDraft = (nextRuns) => {
    if (!draftKey) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify(nextRuns));
    } catch {
      // ignore
    }
  };

  const handleDocketFilesAdd = (tempId, files, mode) => {
    if (!tempId || !files?.length) return;
    const additions = Array.from(files).map((file, index) => ({
      id: `doc_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      mode: mode === "view" ? "view" : "sign",
    }));
    setDocketFiles((prev) => {
      return {
        ...prev,
        [tempId]: [...(prev[tempId] || []), ...additions],
      };
    });
  };

  const handleDocketFileModeChange = (tempId, fileId, mode) => {
    setDocketFiles((prev) => ({
      ...prev,
      [tempId]: (prev[tempId] || []).map((item) =>
        item.id === fileId
          ? { ...item, mode: mode === "view" ? "view" : "sign" }
          : item,
      ),
    }));
  };

  const handleDocketFileRemove = (tempId, fileId) => {
    setDocketFiles((prev) => {
      const remaining = (prev[tempId] || []).filter((item) => item.id !== fileId);
      const next = { ...prev };
      if (remaining.length) next[tempId] = remaining;
      else delete next[tempId];
      return next;
    });
  };

  const uploadJobDocket = async (jobId, file) => {
    if (!jobId || !file) return null;
    const safeName = String(file.name || "docket")
      .replace(/[^\w.\- ]+/g, "_")
      .replace(/\s+/g, "_");
    const uniqueId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const storagePath = `jobDockets/${jobId}/${uniqueId}_${safeName}`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file, {
      contentType: file.type || "application/octet-stream",
    });

    const url = await getDownloadURL(storageRef);

    return {
      url,
      storagePath,
      name: file.name || safeName,
      contentType: file.type || "application/octet-stream",
      size: file.size || 0,
    };
  };

  const setRunsAndDraft = (updater) => {
    setRuns((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveDraft(next);
      return next;
    });
  };

  useEffect(() => {
    const gsCompanyIds = Array.from(
      new Set(
        (runs || [])
          .map((r) => r.companyId)
          .filter(Boolean)
          .filter(
            (cid) =>
              getCompanyUiVariant(companies.find((c) => c.id === cid)) ===
              "greenspace",
          ),
      ),
    );

    if (!gsCompanyIds.length) return;

    const unsubs = [];

    gsCompanyIds.forEach((companyId) => {
      if (!jobTypesByCompany[companyId]) {
        const unJT = onSnapshot(
          query(collection(db, "jobTypes"), where("companyId", "==", companyId)),
          (snap) => {
            const list = snap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
            setJobTypesByCompany((prev) => ({ ...prev, [companyId]: list }));
          },
        );
        unsubs.push(unJT);
      }

      if (!customersByCompany[companyId]) {
        const unCU = onSnapshot(
          query(collection(db, "customers"), where("companyId", "==", companyId)),
          (snap) => {
            const list = snap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
            setCustomersByCompany((prev) => ({ ...prev, [companyId]: list }));
          },
        );
        unsubs.push(unCU);
      }

      if (!trucksByCompany[companyId]) {
        const unTR = onSnapshot(
          query(collection(db, "trucks"), where("companyId", "==", companyId)),
          (snap) => {
            const list = snap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((t) => t.active !== false)
              .sort((a, b) =>
                String(a.rego || "").localeCompare(String(b.rego || "")),
              );

            setTrucksByCompany((prev) => ({ ...prev, [companyId]: list }));
          },
        );
        unsubs.push(unTR);
      }
    });

    return () => {
      unsubs.forEach((u) => u && u());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs, companies]);

  const addRun = () => {
    setRunsAndDraft((prev) => {
      const nextIndex = prev.length + 1;
      return [
        ...prev,
        {
          id: `run-${nextIndex}-${Date.now()}`,
          label: `Run #${nextIndex}`,
          companyId: "",
          vehicleId: "",
          stops: [emptyDefaultStop()],
        },
      ];
    });
  };

  const deleteRun = (runId) => {
    setRunsAndDraft((prev) => {
      const next = prev.filter((r) => r.id !== runId);
      if (!next.length) {
        return [
          {
            id: "run-1",
            label: "Run #1",
            companyId: "",
            vehicleId: "",
            stops: [emptyDefaultStop()],
          },
        ];
      }
      return next.map((r, idx) => ({ ...r, label: `Run #${idx + 1}` }));
    });
  };

  const setRunCompany = (runId, companyId) => {
    setRunsAndDraft((prev) =>
      prev.map((r) => {
        if (r.id !== runId) return r;
        const company = companies.find((c) => c.id === companyId) || null;
        const emptyStop = makeEmptyStopForCompany(company);
        const isGS = getCompanyUiVariant(company) === "greenspace";

        const nextStops = (r.stops || []).map((s) => ({
          ...emptyStop,
          address: s.address || "",
          eta: s.eta || "",
          description: s.description || "",
          poNumber: s.poNumber || "",
          epaWasteTracker: s.epaWasteTracker || "",
          siteContact: s.siteContact || "",
          sitePhone: s.sitePhone || "",
          siteEmail: s.siteEmail || "",
          fixed: !!s.fixed,
          customerId: s.customerId || "",
          siteId: s.siteId || "",
          jobTypeId: s.jobTypeId || "",
          siteLabel: s.siteLabel || "",
          _tempId: s._tempId || createTempId(),
        }));

        return {
          ...r,
          companyId,
          vehicleId: isGS ? r.vehicleId || "" : "",
          stops: nextStops.length ? nextStops : [emptyStop],
        };
      }),
    );
  };

  const setRunVehicle = (runId, vehicleId) => {
    setRunsAndDraft((prev) =>
      prev.map((r) => (r.id === runId ? { ...r, vehicleId } : r)),
    );
  };

  const addStop = (runId) => {
    setRunsAndDraft((prev) =>
      prev.map((r) => {
        if (r.id !== runId) return r;
        const company = companies.find((c) => c.id === r.companyId) || null;
        return {
          ...r,
          stops: [...(r.stops || []), makeEmptyStopForCompany(company)],
        };
      }),
    );
  };

  const deleteStop = (runId, idx) => {
    // Stop "open" state is tracked by array index, which shifts when a stop
    // is removed - collapse everything back to default rather than risk
    // showing the wrong stop expanded.
    setOpenStopKeys(new Set());
    setRunsAndDraft((prev) =>
      prev.map((r) => {
        if (r.id !== runId) return r;
        const removedStop = (r.stops || [])[idx];
        if (removedStop?._tempId) {
          setDocketFiles((prevFiles) => {
            const nextFiles = { ...prevFiles };
            delete nextFiles[removedStop._tempId];
            return nextFiles;
          });
        }
        const nextStops = (r.stops || []).filter((_, i) => i !== idx);
        const company = companies.find((c) => c.id === r.companyId) || null;
        return {
          ...r,
          stops: nextStops.length ? nextStops : [makeEmptyStopForCompany(company)],
        };
      }),
    );
  };

  const handleStopChange = (runId, idx, field, value) => {
    setRunsAndDraft((prev) =>
      prev.map((r) => {
        if (r.id !== runId) return r;
        const list = [...(r.stops || [])];
        const item = { ...(list[idx] || {}) };
        item[field] = value;
        list[idx] = item;
        return { ...r, stops: list };
      }),
    );
  };

  const optimizeRoute = async (runId) => {
    const run = runs.find((r) => r.id === runId);
    if (!run) return;
    // Reordering shifts stop indices - collapse everything rather than risk
    // showing the wrong stop expanded.
    setOpenStopKeys(new Set());

    const list = run.stops || [];
    const cleaned = list.filter((s) => String(s.address || "").trim());

    if (cleaned.length < 2) {
      alert("Need at least 2 addresses to optimize.");
      return;
    }
    if (!window.google?.maps?.DirectionsService) {
      alert("Maps API is not ready yet.");
      return;
    }

    const startTimeStr = prompt(
      "Enter start time (HH:mm)",
      cleaned[0]?.eta || "08:00",
    );
    if (!startTimeStr || !startTimeStr.includes(":")) return;

    const [sh, sm] = startTimeStr.split(":").map(Number);
    let t = new Date();
    t.setHours(sh, sm, 0, 0);

    const origin = cleaned[0].address || "";
    const waypoints = cleaned
      .slice(1)
      .map((s) => ({ location: s.address, stopover: true }));

    const svc = new window.google.maps.DirectionsService();
    svc.route(
      {
        origin,
        destination: origin,
        waypoints,
        optimizeWaypoints: true,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status !== "OK") {
          alert("Could not optimize route.");
          return;
        }
        const order = result.routes[0].waypoint_order || [];

        const reordered = [{ ...cleaned[0], eta: t.toTimeString().slice(0, 5) }];

        order.forEach((wpIdx, i) => {
          const sec = result.routes[0].legs[i]?.duration?.value || 0;
          t = new Date(t.getTime() + sec * 1000);
          reordered.push({
            ...cleaned[wpIdx + 1],
            eta: t.toTimeString().slice(0, 5),
          });
        });

        setRunsAndDraft((prev) =>
          prev.map((r) => (r.id === runId ? { ...r, stops: reordered } : r)),
        );

        alert("Route optimized.");
      },
    );
  };

  function buildCleanedRuns() {
    const cleaned = [];
    for (const r of runs || []) {
      const companyId = r.companyId || "";
      const company = companies.find((c) => c.id === companyId) || null;
      const uiVariant = getCompanyUiVariant(company);
      const truck =
        uiVariant === "greenspace"
          ? (trucksByCompany[companyId] || []).find((t) => t.id === r.vehicleId) ||
            null
          : null;

      const stops = (r.stops || [])
        .map((s) => ({
          ...s,
          address: String(s.address || "").trim(),
          sitePhone: String(s.sitePhone || "").trim(),
          siteEmail: String(s.siteEmail || "").trim(),
          siteContact: String(s.siteContact || "").trim(),
          poNumber: String(s.poNumber || "").trim(),
          epaWasteTracker: String(s.epaWasteTracker || "").trim(),
          docketFiles: s?._tempId ? docketFiles[s._tempId] || [] : [],
        }))
        .filter((s) => s.address);

      if (!stops.length) continue;

      cleaned.push({
        ...r,
        companyId,
        companyName: company?.name || "",
        uiVariant,
        vehicleId: uiVariant === "greenspace" ? r.vehicleId || "" : "",
        vehicleRego: truck?.rego || "",
        vehicleLoadType: truck?.loadType || "",
        vehicleLabel: truck ? getTruckDisplayLabel(truck) : "",
        stops,
      });
    }
    return cleaned;
  }

  function buildTargetsForRecurring(cleanedRuns) {
    const targetsMap = {};

    for (const r of cleanedRuns || []) {
      const companyId = r.companyId;
      const companyName = r.companyName || "";
      const uiVariant = r.uiVariant || "default";

      if (!targetsMap[companyId]) {
        targetsMap[companyId] = {
          companyId,
          companyName,
          uiVariant,
          runsTemplate: {},
          runMeta: {},
        };
      }

      const runKey = r.label;
      const jtList = jobTypesByCompany[companyId] || [];
      const cuList = customersByCompany[companyId] || [];

      targetsMap[companyId].runMeta[runKey] = {
        vehicleId: r.vehicleId || "",
        vehicleRego: r.vehicleRego || "",
        vehicleLoadType: r.vehicleLoadType || "",
        vehicleLabel: r.vehicleLabel || "",
      };

      targetsMap[companyId].runsTemplate[runKey] = (r.stops || []).map((stop) => {
        const base = {
          address: stop.address || "",
          eta: stop.eta || "",
          description: stop.description || "",
          poNumber: stop.poNumber || "",
          epaWasteTracker: stop.epaWasteTracker || "",
          siteContact: stop.siteContact || "",
          sitePhone: stop.sitePhone || "",
          siteEmail: stop.siteEmail || "",
          fixed: !!stop.fixed,
        };

        if (uiVariant === "greenspace") {
          const jt = jtList.find((j) => j.id === stop.jobTypeId);
          const customer = cuList.find((c) => c.id === stop.customerId);

          return {
            ...base,
            customerId: stop.customerId || "",
            customerName: customer?.name || "",
            siteId: stop.siteId || "",
            siteLabel: stop.siteLabel || "",
            jobTypeId: stop.jobTypeId || "",
            jobTypeCode: jt?.code || toAbbrevCode(jt?.name),
            jobTypeName: jt?.name || "",
          };
        }

        return base;
      });
    }

    return Object.values(targetsMap);
  }

  useEffect(() => {
    if (typeof onContextChange !== "function") return;

    const cleanedRuns = buildCleanedRuns();
    const targets = buildTargetsForRecurring(cleanedRuns);

    onContextChange({
      driverUid,
      driverEmail: driver?.email || "",
      localDate,
      cleanedRuns,
      targets,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverUid, localDate, runs, companies, driver, docketFiles, trucksByCompany]);

  async function saveAll() {
    if (!driverUid || !localDate) {
      alert("Please select a driver and date.");
      return;
    }
    const chosenDriver = driver || drivers.find((d) => d.uid === driverUid) || null;
    if (!chosenDriver) {
      alert("Invalid driver selected.");
      return;
    }

    const cleanedRuns = buildCleanedRuns();
    if (!cleanedRuns.length) {
      alert("Please add at least one address.");
      return;
    }

    const missingCompany = cleanedRuns.some((r) => !r.companyId);
    if (missingCompany) {
      alert("Please choose a company for every run.");
      return;
    }

    const uid = chosenDriver.uid;
    const legacyAssignmentId = `${uid}_${localDate}`;

    let alreadyAssigned = false;
    const legacySnap = await getDoc(doc(db, "assignments", legacyAssignmentId));
    if (legacySnap.exists()) alreadyAssigned = true;

    if (alreadyAssigned) {
      const ok = window.confirm(
        "An assignment for this driver and date already exists.\n\nSaving will overwrite it.\n\nContinue?",
      );
      if (!ok) return;
    }

    const combinedRunsWithIds = {};
    const combinedRunMeta = {};
    const perCompany = {};

    for (const r of cleanedRuns) {
      const runKey = r.label;
      combinedRunsWithIds[runKey] = [];
      combinedRunMeta[runKey] = {
        companyId: r.companyId,
        companyName: r.companyName,
        uiVariant: r.uiVariant,
        vehicleId: r.vehicleId || "",
        vehicleRego: r.vehicleRego || "",
        vehicleLoadType: r.vehicleLoadType || "",
        vehicleLabel: r.vehicleLabel || "",
      };

      if (!perCompany[r.companyId]) {
        perCompany[r.companyId] = {
          companyId: r.companyId,
          companyName: r.companyName,
          uiVariant: r.uiVariant,
          runsWithIds: {},
          runMeta: {},
        };
      }
      perCompany[r.companyId].runsWithIds[runKey] = [];
      perCompany[r.companyId].runMeta[runKey] = {
        companyId: r.companyId,
        companyName: r.companyName,
        uiVariant: r.uiVariant,
        vehicleId: r.vehicleId || "",
        vehicleRego: r.vehicleRego || "",
        vehicleLoadType: r.vehicleLoadType || "",
        vehicleLabel: r.vehicleLabel || "",
      };

      const jobTypes = jobTypesByCompany[r.companyId] || [];
      const customers = customersByCompany[r.companyId] || [];

      for (const stop of r.stops) {
        const isGS = r.uiVariant === "greenspace";

        const jt = isGS ? jobTypes.find((j) => j.id === stop.jobTypeId) : null;
        const customer = isGS ? customers.find((c) => c.id === stop.customerId) : null;

        const jobPayload = {
          companyId: r.companyId,
          companyName: r.companyName,
          driverId: uid,
          driverEmail: chosenDriver.email || "",
          date: localDate,
          runKey,
          vehicleId: r.vehicleId || "",
          vehicleRego: r.vehicleRego || "",
          vehicleLoadType: r.vehicleLoadType || "",
          vehicleLabel: r.vehicleLabel || "",
          address: stop.address || "",
          eta: stop.eta || "",
          description: stop.description || "",
          poNumber: stop.poNumber || "",
          epaWasteTracker: stop.epaWasteTracker || "",
          siteContact: stop.siteContact || "",
          sitePhone: stop.sitePhone || "",
          siteEmail: stop.siteEmail || "",
          fixed: !!stop.fixed,
          customerId: stop.customerId || "",
          customerName: customer?.name || stop.customerName || "",
          siteId: stop.siteId || "",
          siteLabel: stop.siteLabel || "",
          jobTypeId: stop.jobTypeId || "",
          jobTypeCode: jt?.code || (isGS ? toAbbrevCode(jt?.name) : ""),
          jobTypeName: jt?.name || "",
          uiVariant: r.uiVariant,
          assignedSource: "mixed-assign",
          status: "assigned",
          createdAt: Date.now(),
          updatedAt: Date.now(),
          docketUrl: "",
          docketStoragePath: "",
          docketFileName: "",
          docketContentType: "",
          docketMode: stop.docketMode === "view" ? "view" : "sign",
          jobDocuments: [],
          signedJobDocuments: {},
        };

        const jobRef = await addDoc(collection(db, "jobs"), jobPayload);

        const uploadedDocuments = [];
        for (const item of stop.docketFiles || []) {
          const uploaded = await uploadJobDocket(jobRef.id, item.file);
          if (uploaded) {
            uploadedDocuments.push({
              id: item.id,
              ...uploaded,
              mode: item.mode === "view" ? "view" : "sign",
            });
          }
        }

        const legacyDocket =
          uploadedDocuments.find((item) => item.mode === "sign") ||
          uploadedDocuments[0] ||
          null;

        if (uploadedDocuments.length) {
          await setDoc(
            doc(db, "jobs", jobRef.id),
            {
              jobDocuments: uploadedDocuments,
              docketUrl: legacyDocket.url,
              docketStoragePath: legacyDocket.storagePath,
              docketFileName: legacyDocket.name,
              docketContentType: legacyDocket.contentType,
              docketMode: legacyDocket.mode,
              docketUploadedAt: serverTimestamp(),
            },
            { merge: true },
          );
        }

        const stopForAssignment = {
          jobId: jobRef.id,
          address: jobPayload.address,
          eta: jobPayload.eta,
          description: jobPayload.description,
          poNumber: jobPayload.poNumber,
          epaWasteTracker: jobPayload.epaWasteTracker,
          siteContact: jobPayload.siteContact,
          sitePhone: jobPayload.sitePhone,
          siteEmail: jobPayload.siteEmail,
          fixed: jobPayload.fixed,
          jobDocuments: uploadedDocuments,
          docketUrl: legacyDocket?.url || "",
          docketFileName: legacyDocket?.name || "",
          docketContentType: legacyDocket?.contentType || "",
          docketMode: legacyDocket?.mode || jobPayload.docketMode,
          customerId: jobPayload.customerId,
          customerName: jobPayload.customerName,
          siteId: jobPayload.siteId,
          siteLabel: jobPayload.siteLabel,
          jobTypeId: jobPayload.jobTypeId,
          jobTypeCode: jobPayload.jobTypeCode,
          jobTypeName: jobPayload.jobTypeName,
          companyId: jobPayload.companyId,
          companyName: jobPayload.companyName,
          uiVariant: jobPayload.uiVariant,
          vehicleId: jobPayload.vehicleId,
          vehicleRego: jobPayload.vehicleRego,
          vehicleLoadType: jobPayload.vehicleLoadType,
          vehicleLabel: jobPayload.vehicleLabel,
        };

        combinedRunsWithIds[runKey].push(stopForAssignment);
        perCompany[r.companyId].runsWithIds[runKey].push(stopForAssignment);
      }
    }

    const combinedAssignmentPayload = {
      driverId: uid,
      driverEmail: chosenDriver.email || "",
      date: localDate,
      companyId: "mixed",
      companyName: "Mixed",
      uiVariant: "mixed",
      runs: combinedRunsWithIds,
      runMeta: combinedRunMeta,
      updatedAt: Date.now(),
    };

    await setDoc(doc(db, "assignments", legacyAssignmentId), combinedAssignmentPayload, {
      merge: false,
    });

    for (const companyId of Object.keys(perCompany)) {
      const scopedId = `${companyId}_${uid}_${localDate}`;
      const block = perCompany[companyId];

      await setDoc(
        doc(db, "assignments", scopedId),
        {
          driverId: uid,
          driverEmail: chosenDriver.email || "",
          date: localDate,
          companyId: block.companyId,
          companyName: block.companyName,
          uiVariant: block.uiVariant,
          runs: block.runsWithIds,
          runMeta: block.runMeta,
          updatedAt: Date.now(),
          createdAt: serverTimestamp(),
        },
        { merge: false },
      );
    }

    if (draftKey) localStorage.removeItem(draftKey);
    setDocketFiles({});

    alert("Saved! Runs were created per company and sent to the driver.");
  }

  const copyToClipboard = async (text) => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
  };

  const totalStops = useMemo(
    () => (runs || []).reduce((sum, run) => sum + (run.stops?.length || 0), 0),
    [runs],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className={`${fieldWrapClass} min-w-[220px] flex-1`}>
          <span className={labelClass}>Driver</span>
          <LiveSearchSelect
            value={driverUid}
            onChange={setDriverUid}
            options={driverOptions}
            placeholder="Search drivers..."
            emptyText="No drivers found."
          />
        </div>

        <div className={fieldWrapClass}>
          <span className={labelClass}>Date</span>
          <input
            type="date"
            value={localDate}
            onChange={(e) => setLocalDate(e.target.value)}
            className={inputClass}
          />
        </div>

        <div className="ml-auto text-xs text-gray-500">
          {driver ? (
            <>
              Assigned to <span className="font-semibold text-gray-900">{driver.email}</span>
              {" · "}
            </>
          ) : (
            "Select a driver to start assigning jobs · "
          )}
          {runs.length} runs · {totalStops} stops · Maps {scriptLoaded ? "ready" : "loading"}
        </div>
      </div>

      <div className="space-y-5">
        {(runs || []).map((r, runIndex) => {
          const company = companies.find((c) => c.id === r.companyId) || null;
          const uiVariant = getCompanyUiVariant(company);
          const isGS = uiVariant === "greenspace";
          const jobTypes = jobTypesByCompany[r.companyId] || [];
          const customers = customersByCompany[r.companyId] || [];
          const trucks = trucksByCompany[r.companyId] || [];
          const selectedTruck = trucks.find((t) => t.id === r.vehicleId) || null;

          return (
            <div
              key={r.id}
              className="overflow-hidden rounded-[26px] border border-gray-200 bg-white shadow-sm"
            >
              <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-black text-sm font-semibold text-white shadow-sm">
                      {runIndex + 1}
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                        Run
                      </div>
                      <div className="text-lg font-semibold text-gray-950">
                        Run #{runIndex + 1}
                      </div>
                    </div>
                    <StatPill tone={isGS ? "emerald" : "blue"}>
                      {isGS ? "Greenspace layout" : "Default layout"}
                    </StatPill>
                    <StatPill>
                      {(r.stops || []).length} stop{(r.stops || []).length === 1 ? "" : "s"}
                    </StatPill>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => optimizeRoute(r.id)}
                      className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                    >
                      Optimize route
                    </button>
                    <button
                      onClick={() => deleteRun(r.id)}
                      className="inline-flex items-center rounded-xl border border-red-200 bg-red-50 px-3.5 py-2 text-sm font-medium text-red-600 transition hover:bg-red-100"
                    >
                      Delete run
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-5 px-5 py-5 sm:px-6">
                <SectionCard
                  title="Run settings"
                  subtitle="Choose which company this run belongs to and assign a vehicle when Greenspace layout is used."
                >
                  <div className={`grid gap-4 ${isGS ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
                    <div className={fieldWrapClass}>
                      <span className={labelClass}>Company for this run</span>
                      <select
                        value={r.companyId || ""}
                        onChange={(e) => setRunCompany(r.id, e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Select company</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500">
                        This run will use {isGS ? "Greenspace" : "Default"} fields.
                      </p>
                    </div>

                    {isGS && (
                      <div className={fieldWrapClass}>
                        <span className={labelClass}>Vehicle for this run</span>
                        <select
                          value={r.vehicleId || ""}
                          onChange={(e) => setRunVehicle(r.id, e.target.value)}
                          className={inputClass}
                          disabled={!r.companyId}
                        >
                          <option value="">Select vehicle</option>
                          {trucks.map((t) => (
                            <option key={t.id} value={t.id}>
                              {getTruckDisplayLabel(t)}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500">
                          {selectedTruck
                            ? `Selected: ${getTruckDisplayLabel(selectedTruck)}`
                            : trucks.length
                              ? "Choose the truck used for this run."
                              : "No active trucks found in Trucks tab."}
                        </p>
                      </div>
                    )}
                  </div>
                </SectionCard>

                <div className="space-y-4">
                  {(r.stops || []).map((stop, idx) => {
                    const key = `${r.id}-${idx}`;
                    const selectedCustomer = isGS
                      ? customers.find((x) => x.id === stop.customerId)
                      : null;
                    const customerSites =
                      isGS && Array.isArray(selectedCustomer?.sites)
                        ? selectedCustomer.sites
                        : [];
                    const selectedSite = isGS
                      ? customerSites.find((x) => x.id === stop.siteId)
                      : null;

                    const stopIsOpen = openStopKeys.has(`${r.id}__${idx}`);
                    const stopServiceLabel = isGS
                      ? jobTypes.find((jt) => jt.id === stop.jobTypeId)?.name || ""
                      : "";

                    return (
                      <div
                        key={key}
                        className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50/70"
                      >
                        <div className="flex flex-col gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                          <button
                            type="button"
                            onClick={() => toggleStopOpen(r.id, idx)}
                            aria-expanded={stopIsOpen}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          >
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-sm font-semibold text-emerald-700">
                              {idx + 1}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900">Stop #{idx + 1}</div>
                              <div className="truncate text-xs text-gray-500">
                                {stopIsOpen
                                  ? stop.address || "No address selected yet"
                                  : [
                                      stop.address || "No address selected yet",
                                      selectedCustomer?.name,
                                      stopServiceLabel,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                              </div>
                            </div>
                            <svg
                              className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${stopIsOpen ? "rotate-180" : ""}`}
                              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </button>

                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                                stop.fixed
                                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                              }`}
                              onClick={() =>
                                handleStopChange(r.id, idx, "fixed", !stop.fixed)
                              }
                            >
                              {stop.fixed ? "Pinned stop" : "Flexible stop"}
                            </button>
                            <button
                              onClick={() => deleteStop(r.id, idx)}
                              className="rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-100"
                            >
                              Remove stop
                            </button>
                          </div>
                        </div>

                        {stopIsOpen && (
                        <div className="space-y-4 p-4">
                          {isGS && (
                            <SectionCard
                              title="Customer details"
                              subtitle="Link the stop to a customer, site, and services."
                            >
                              <div className="grid gap-4 md:grid-cols-3">
                                <div className={fieldWrapClass}>
                                  <span className={labelClass}>Customer</span>
                                  <SearchableSelect
                                    value={stop.customerId || ""}
                                    onChange={(id) => {
                                      handleStopChange(r.id, idx, "customerId", id);
                                      handleStopChange(r.id, idx, "siteId", "");
                                      handleStopChange(r.id, idx, "siteLabel", "");
                                      handleStopChange(r.id, idx, "siteContact", "");
                                      handleStopChange(r.id, idx, "sitePhone", "");
                                      handleStopChange(r.id, idx, "siteEmail", "");
                                    }}
                                    options={customers.map((c) => ({
                                      value: c.id,
                                      label: c.name || "(unnamed customer)",
                                      keywords: [c.name, c.accountsEmail, c.accountName],
                                    }))}
                                    placeholder="Select customer"
                                    searchPlaceholder="Search customer"
                                    disabled={!r.companyId}
                                    emptyText="No customers found."
                                  />
                                </div>

                                <div className={fieldWrapClass}>
                                  <span className={labelClass}>Site</span>
                                  <SearchableSelect
                                    value={stop.siteId || ""}
                                    onChange={(siteId) => {
                                      handleStopChange(r.id, idx, "siteId", siteId);
                                      const site = customerSites.find((x) => x.id === siteId);
                                      handleStopChange(r.id, idx, "siteLabel", site?.label || "");
                                      handleStopChange(
                                        r.id,
                                        idx,
                                        "siteContact",
                                        getSiteContactName(site),
                                      );
                                      handleStopChange(
                                        r.id,
                                        idx,
                                        "sitePhone",
                                        getSiteContactPhone(site),
                                      );
                                      handleStopChange(
                                        r.id,
                                        idx,
                                        "siteEmail",
                                        getSiteContactEmail(site),
                                      );
                                      handleStopChange(r.id, idx, "address", site?.address || "");
                                    }}
                                    options={customerSites.map((s) => ({
                                      value: s.id,
                                      label: s.label || "(unnamed site)",
                                      keywords: [
                                        s.label,
                                        s.siteName,
                                        s.address,
                                        s.contactName,
                                        s.contactPhone,
                                        s.contactEmail,
                                        s.bookingContactName,
                                        s.bookingContactPhone,
                                        s.bookingContactEmail,
                                      ],
                                    }))}
                                    placeholder="Select site"
                                    searchPlaceholder="Search site"
                                    disabled={!stop.customerId}
                                    emptyText="No sites found."
                                  />
                                </div>

                                <div className={fieldWrapClass}>
                                  <span className={labelClass}>Services</span>
                                  <select
                                    className={inputClass}
                                    value={stop.jobTypeId || ""}
                                    onChange={(e) =>
                                      handleStopChange(r.id, idx, "jobTypeId", e.target.value)
                                    }
                                    disabled={!r.companyId}
                                  >
                                    <option value="">Select Services</option>
                                    {jobTypes.map((j) => (
                                      <option key={j.id} value={j.id}>
                                        {j.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {selectedSite ? (
                                <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-900">
                                  <div className="font-medium">Selected site details</div>
                                  <div className="mt-1 text-xs leading-6 text-emerald-800">
                                    <span className="font-medium">Contact:</span> {getSiteContactName(selectedSite) || "-"}
                                    <span className="mx-2 text-emerald-300">•</span>
                                    <span className="font-medium">Phone:</span> {getSiteContactPhone(selectedSite) || "-"}
                                    <span className="mx-2 text-emerald-300">•</span>
                                    <span className="font-medium">Email:</span> {getSiteContactEmail(selectedSite) || "-"}
                                  </div>
                                </div>
                              ) : null}
                            </SectionCard>
                          )}

                          <SectionCard
                            title="Location and scheduling"
                            subtitle="Choose the stop address and time information."
                          >
                            <div className="space-y-4">
                              <div className={fieldWrapClass}>
                                <span className={labelClass}>Address</span>
                                <Autocomplete
                                  onLoad={(ref) => {
                                    acRefs.current[key] = ref;
                                  }}
                                  onPlaceChanged={() => {
                                    const place = acRefs.current[key]?.getPlace();
                                    const line =
                                      place?.formatted_address ||
                                      place?.name ||
                                      stop.address ||
                                      "";
                                    handleStopChange(r.id, idx, "address", line);
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="Start typing address"
                                    value={stop.address || ""}
                                    onChange={(e) =>
                                      handleStopChange(r.id, idx, "address", e.target.value)
                                    }
                                    className={inputClass}
                                  />
                                </Autocomplete>
                              </div>

                              <div className="grid gap-4 sm:grid-cols-3">
                                <div className={fieldWrapClass}>
                                  <span className={labelClass}>ETA</span>
                                  <input
                                    type="time"
                                    value={stop.eta || ""}
                                    onChange={(e) =>
                                      handleStopChange(r.id, idx, "eta", e.target.value)
                                    }
                                    className={inputClass}
                                  />
                                </div>

                                <div className={fieldWrapClass}>
                                  <span className={labelClass}>PO number</span>
                                  <input
                                    value={stop.poNumber || ""}
                                    onChange={(e) =>
                                      handleStopChange(r.id, idx, "poNumber", e.target.value)
                                    }
                                    className={inputClass}
                                  />
                                </div>

                                <div className={fieldWrapClass}>
                                  <span className={labelClass}>EPA Waste Tracker</span>
                                  <input
                                    value={stop.epaWasteTracker || ""}
                                    onChange={(e) =>
                                      handleStopChange(
                                        r.id,
                                        idx,
                                        "epaWasteTracker",
                                        e.target.value,
                                      )
                                    }
                                    className={inputClass}
                                  />
                                </div>
                              </div>
                            </div>
                          </SectionCard>

                          <SectionCard
                            title="Site contact"
                            subtitle="Keep all contact details together for the driver."
                          >
                            <div className="grid gap-4 sm:grid-cols-3">
                              <div className={fieldWrapClass}>
                                <span className={labelClass}>Site contact</span>
                                <input
                                  value={stop.siteContact || ""}
                                  onChange={(e) =>
                                    handleStopChange(r.id, idx, "siteContact", e.target.value)
                                  }
                                  className={inputClass}
                                />
                              </div>

                              <div className={fieldWrapClass}>
                                <div className="flex items-center justify-between">
                                  <span className={labelClass}>Site phone</span>
                                  {stop.sitePhone ? (
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(stop.sitePhone)}
                                      className="text-[11px] font-medium text-gray-500 transition hover:text-gray-900"
                                      title="Copy phone"
                                    >
                                      Copy
                                    </button>
                                  ) : null}
                                </div>
                                <input
                                  value={stop.sitePhone || ""}
                                  onChange={(e) =>
                                    handleStopChange(r.id, idx, "sitePhone", e.target.value)
                                  }
                                  placeholder="e.g. 04xx xxx xxx"
                                  className={inputClass}
                                />
                              </div>

                              <div className={fieldWrapClass}>
                                <div className="flex items-center justify-between">
                                  <span className={labelClass}>Site email</span>
                                  {stop.siteEmail ? (
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(stop.siteEmail)}
                                      className="text-[11px] font-medium text-gray-500 transition hover:text-gray-900"
                                      title="Copy email"
                                    >
                                      Copy
                                    </button>
                                  ) : null}
                                </div>
                                <input
                                  value={stop.siteEmail || ""}
                                  onChange={(e) =>
                                    handleStopChange(r.id, idx, "siteEmail", e.target.value)
                                  }
                                  placeholder="e.g. site@customer.com"
                                  className={inputClass}
                                />
                              </div>
                            </div>
                          </SectionCard>

                          <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
                            <SectionCard
                              title="Job documents"
                              subtitle="Attach dockets, instruction files, or location photos."
                              right={
                                (docketFiles[stop._tempId] || []).length ? (
                                  <StatPill tone="emerald">
                                    {docketFiles[stop._tempId].length} ready
                                  </StatPill>
                                ) : null
                              }
                            >
                              <div className="flex flex-col gap-3">
                                <select
                                  value={stop.docketMode || "sign"}
                                  onChange={(e) =>
                                    handleStopChange(
                                      r.id,
                                      idx,
                                      "docketMode",
                                      e.target.value,
                                    )
                                  }
                                  className={`${inputClass} max-w-xs`}
                                >
                                  <option value="sign">Signature required</option>
                                  <option value="view">View only / instructions</option>
                                </select>
                                <div className="flex flex-wrap items-center gap-3">
                                <label className="inline-flex cursor-pointer items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50">
                                  <input
                                    type="file"
                                    accept={
                                      stop.docketMode === "view"
                                        ? "image/*,application/pdf"
                                        : "application/pdf"
                                  }
                                  multiple
                                  className="hidden"
                                  onChange={(e) => {
                                    handleDocketFilesAdd(
                                      stop._tempId,
                                      e.target.files,
                                      stop.docketMode,
                                    );
                                    e.target.value = "";
                                  }}
                                />
                                  Add files or photos
                                </label>

                                {!(docketFiles[stop._tempId] || []).length &&
                                stop.docketFileName ? (
                                  <span className="text-sm text-gray-500">
                                    Existing: {stop.docketFileName}
                                  </span>
                                ) : !(docketFiles[stop._tempId] || []).length ? (
                                  <span className="text-sm text-gray-400">
                                    {stop.docketMode === "view" ? "PDF or image" : "PDF"}
                                  </span>
                                ) : null}
                                </div>
                                {(docketFiles[stop._tempId] || []).map((item) => {
                                  const canSign =
                                    /pdf/i.test(item.file?.type || "") ||
                                    /\.pdf$/i.test(item.file?.name || "");
                                  return (
                                    <div
                                      key={item.id}
                                      className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2"
                                    >
                                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-700">
                                        {item.file.name}
                                      </span>
                                      <select
                                        value={item.mode}
                                        onChange={(e) =>
                                          handleDocketFileModeChange(
                                            stop._tempId,
                                            item.id,
                                            e.target.value,
                                          )
                                        }
                                        className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs"
                                      >
                                        <option value="sign" disabled={!canSign}>
                                          Signature required
                                        </option>
                                        <option value="view">View only</option>
                                      </select>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleDocketFileRemove(stop._tempId, item.id)
                                        }
                                        className="text-xs font-medium text-red-600 hover:text-red-700"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </SectionCard>

                            <SectionCard
                              title="Description"
                              subtitle="Add notes that will help the driver complete the stop."
                            >
                              <textarea
                                value={stop.description || ""}
                                onChange={(e) =>
                                  handleStopChange(r.id, idx, "description", e.target.value)
                                }
                                className={`${inputClass} min-h-[120px] resize-y`}
                              />
                            </SectionCard>
                          </div>
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <SectionCard
                  title="Map and actions"
                  subtitle="Add stops from the map or use quick actions below."
                >
                  <DriverMap
                    addresses={r.stops || []}
                    onMapClick={({ address }) => {
                      setRunsAndDraft((prev) =>
                        prev.map((x) => {
                          if (x.id !== r.id) return x;
                          const company = companies.find((c) => c.id === x.companyId) || null;
                          return {
                            ...x,
                            stops: [
                              ...(x.stops || []),
                              { ...makeEmptyStopForCompany(company), address },
                            ],
                          };
                        }),
                      );
                    }}
                  />

                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      onClick={() => addStop(r.id)}
                      className="inline-flex items-center rounded-xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:bg-gray-900"
                    >
                      + Add address
                    </button>
                    <button
                      onClick={() => optimizeRoute(r.id)}
                      className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 transition hover:bg-gray-50"
                    >
                      Optimize route
                    </button>
                  </div>
                </SectionCard>
              </div>
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-4 z-10">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white/95 px-4 py-4 shadow-lg backdrop-blur">
          <div>
            <div className="text-sm font-semibold text-gray-900">Ready to keep building?</div>
            <div className="text-xs text-gray-500">
              Add another run or save all assignments for the selected driver.
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={addRun}
              className="inline-flex items-center rounded-xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:bg-gray-900"
            >
              + Add run
            </button>
            <button
              onClick={saveAll}
              className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={!driverUid || !localDate}
            >
              Save all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
